import io from 'socket.io-client';

class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.currentChatId = null;
    this.userId = null;
    this.username = null;

    // Callbacks
    this.onMessageReceived = null;
    this.onTypingStatus = null;
    this.onMessagesRead = null;
    this.onError = null;
    this.onConnected = null;
    this.onDisconnected = null;
  }

  // Initialize socket connection
  initialize(serverUrl = 'https://api.utiliti.ng') {
    try {
      this.socket = io(serverUrl, {
        transports: ['websocket', 'polling'],
        autoConnect: false,
        forceNew: true,
        timeout: 20000,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        maxReconnectionAttempts: 5,
      });

      this.setupEventListeners();
      console.log('Socket.IO initialized with server:', serverUrl);
    } catch (error) {
      console.error('Error initializing Socket.IO:', error);
      if (this.onError) {
        this.onError('Failed to initialize socket connection');
      }
    }
  }

  // Setup event listeners
  setupEventListeners() {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('Socket connected');
      this.isConnected = true;
      if (this.onConnected) {
        this.onConnected();
      }
    });

    this.socket.on('disconnect', () => {
      console.log('Socket disconnected');
      this.isConnected = false;
      if (this.onDisconnected) {
        this.onDisconnected();
      }
    });

    this.socket.on('connect_error', (error) => {
      console.log('Socket connection error:', error);
      this.isConnected = false;
      if (this.onError) {
        this.onError(`Connection failed: ${error}`);
      }
    });

    this.socket.on('authenticated', (data) => {
      console.log('Authentication response:', data);
      if (data.success === true) {
        console.log('Successfully authenticated with socket');
      } else {
        if (this.onError) {
          this.onError(data.error || 'Authentication failed');
        }
      }
    });

    this.socket.on('joinedChat', (data) => {
      console.log('Joined chat:', data.chatId);
    });

    this.socket.on('newMessage', (data) => {
      console.log('New message received:', data.message?.content);
      if (this.onMessageReceived) {
        this.onMessageReceived(data);
      }
    });

    this.socket.on('userTyping', (data) => {
      console.log('Socket: Received typing status:', data.sender, '-', data.isTyping);
      if (this.onTypingStatus) {
        console.log('Socket: Calling onTypingStatus callback');
        this.onTypingStatus(data);
      } else {
        console.log('Socket: No onTypingStatus callback registered');
      }
    });

    this.socket.on('messagesRead', (data) => {
      console.log('Messages read:', data.messageIds);
      if (this.onMessagesRead) {
        this.onMessagesRead(data);
      }
    });

    this.socket.on('messageError', (data) => {
      console.log('Message error:', data.error);
      if (this.onError) {
        this.onError(data.error);
      }
    });

    this.socket.on('readError', (data) => {
      console.log('Read error:', data.error);
      if (this.onError) {
        this.onError(data.error);
      }
    });
  }

  // Connect to server
  connect() {
    if (this.socket && !this.isConnected) {
      this.socket.connect();
    }
  }

  // Disconnect from server
  disconnect() {
    if (this.socket && this.isConnected) {
      this.socket.disconnect();
      this.isConnected = false;
    }
  }

  // Authenticate user
  authenticate(userId, username) {
    if (this.socket && this.isConnected) {
      this.userId = userId;
      this.username = username;
      this.socket.emit('authenticate', {
        userId: userId,
        username: username,
      });
      console.log('Authenticating user:', username);
    }
  }

  // Join a chat room
  joinChat(chatId) {
    if (this.socket && this.isConnected) {
      this.currentChatId = chatId;
      this.socket.emit('joinChat', { chatId: chatId });
      console.log('Joining chat:', chatId);
    }
  }

  // Leave current chat room
  leaveChat() {
    if (this.socket && this.isConnected && this.currentChatId) {
      this.socket.emit('leaveChat', { chatId: this.currentChatId });
      console.log('Leaving chat:', this.currentChatId);
      this.currentChatId = null;
    }
  }

  // Send a text message
  sendMessage(chatId, content, receiver, replyTo = null) {
    if (this.socket && this.isConnected && this.username) {
      this.socket.emit('sendMessage', {
        chatId: chatId,
        sender: this.username,
        content: content,
        receiver: receiver,
        replyTo: replyTo,
      });
      console.log('Sending message to chat', chatId, ':', content);
    }
  }

  // Send a media message
  sendMediaMessage(chatId, receiver, mediaUrl, mediaFileName, mediaSize, mediaMimeType, messageType) {
    if (this.socket && this.isConnected && this.username) {
      this.socket.emit('sendMediaMessage', {
        chatId: chatId,
        sender: this.username,
        receiver: receiver,
        mediaUrl: mediaUrl,
        mediaFileName: mediaFileName,
        mediaSize: mediaSize,
        mediaMimeType: mediaMimeType,
        messageType: messageType,
      });
      console.log('Sending media message to chat', chatId, ':', mediaFileName);
    }
  }

  // Mark messages as read
  markAsRead(chatId, messageIds) {
    if (this.socket && this.isConnected && this.userId) {
      this.socket.emit('markAsRead', {
        chatId: chatId,
        messageIds: messageIds,
        userId: this.userId,
      });
      console.log('Marking messages as read in chat', chatId);
    }
  }

  // Start typing indicator
  startTyping(chatId) {
    if (this.socket && this.isConnected && this.username) {
      this.socket.emit('startTyping', {
        chatId: chatId,
        sender: this.username,
      });
    }
  }

  // Stop typing indicator
  stopTyping(chatId) {
    if (this.socket && this.isConnected && this.username) {
      this.socket.emit('stopTyping', {
        chatId: chatId,
        sender: this.username,
      });
    }
  }

  // Dispose resources
  dispose() {
    this.disconnect();
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket = null;
    }
    this.isConnected = false;
    this.currentChatId = null;
    this.userId = null;
    this.username = null;
  }
}

// Create singleton instance
const socketService = new SocketService();
export default socketService; 